// ==========================
// Version 16 — db.js (NFT flow cleanup)
// - Keeps final minted status on memories/{id}.nft.latest + nft.mints[]
// - Saves resumable pre-mint checkpoints to memories/{id}/runtime/nftPending
// - Removes temporary staged debug writes after runtime rules fix
// ==========================
import { auth, db, storage } from './firebase';
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  onSnapshot,
  arrayUnion,
  Timestamp,
} from 'firebase/firestore';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  updateMetadata,
} from 'firebase/storage';

/* ---------------------------------
   Small helpers
--------------------------------- */
function isFirebaseUrl(u = '') {
  return (
    typeof u === 'string' &&
    (u.startsWith('gs://') ||
      /^https?:\/\/firebasestorage\.googleapis\.com\//.test(u))
  );
}

function normalizeImageUrls(urls) {
  if (!Array.isArray(urls)) return [];
  return urls.filter((u) => typeof u === 'string' && u.trim().length > 0);
}

function normAddr(a) {
  if (!a) return null;
  const s = String(a).trim();
  if (!s) return null;
  return s.toLowerCase();
}

function toFsTimestampFromIso(iso) {
  try {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return Timestamp.fromDate(d);
  } catch (_) {
    return null;
  }
}

function safeNowTs() {
  try {
    return Timestamp.fromDate(new Date());
  } catch (_) {
    return null;
  }
}

function isMemoryMarkedMintedInData(data) {
  return !!data?.nft?.latest?.txHash;
}

async function syncMemoryMediaVisibility(urls, isMediaPublic) {
  const safeUrls = normalizeImageUrls(urls).filter((u) => isFirebaseUrl(u));
  if (!safeUrls.length) return true;

  const mediaPublicValue = isMediaPublic ? 'true' : 'false';
  const results = await Promise.allSettled(
    safeUrls.map((url) =>
      updateMetadata(ref(storage, url), {
        customMetadata: {
          mediaPublic: mediaPublicValue,
        },
      })
    )
  );

  const failures = results.filter((r) => r.status === 'rejected');
  if (failures.length) {
    const firstErr = failures[0]?.reason;
    throw new Error(
      firstErr?.message || 'Failed to sync Storage media visibility metadata.'
    );
  }
  return true;
}

function normalizeNftMirror(input) {
  if (!input || typeof input !== 'object') return null;

  const providerValue = input.provider ? String(input.provider).trim() : null;
  const metadataCidValue = input.metadataCid ? String(input.metadataCid).trim() : null;
  const imageCidValue = input.imageCid ? String(input.imageCid).trim() : null;
  const tokenUriValue = input.tokenUri ? String(input.tokenUri).trim() : null;
  const gatewayBaseValue = input.gatewayBase ? String(input.gatewayBase).trim() : null;
  const gatewayUrlValue =
    input.gatewayUrl
      ? String(input.gatewayUrl).trim()
      : (gatewayBaseValue && metadataCidValue ? `${gatewayBaseValue.replace(/\/+$/, '')}/${metadataCidValue}` : null);

  const hasValue = providerValue || metadataCidValue || imageCidValue || tokenUriValue || gatewayBaseValue || gatewayUrlValue;
  if (!hasValue) return null;

  return {
    provider: providerValue,
    metadataCid: metadataCidValue,
    imageCid: imageCidValue,
    tokenUri: tokenUriValue,
    gatewayBase: gatewayBaseValue,
    gatewayUrl: gatewayUrlValue,
  };
}

function normalizeVerification(input) {
  if (!input || typeof input !== 'object') return null;

  return {
    status: input.status ? String(input.status) : null,
    ok: typeof input.ok === 'boolean' ? input.ok : null,
    attemptsUsed: Number.isFinite(Number(input.attemptsUsed)) ? Number(input.attemptsUsed) : null,
    message: input.message ? String(input.message) : null,
    metadataUrl: input.metadataUrl ? String(input.metadataUrl) : null,
    imageUrl: input.imageUrl ? String(input.imageUrl) : null,
    error: input.error ? String(input.error) : null,
    verifiedAt: toFsTimestampFromIso(input.verifiedAt) || null,
    failedAt: toFsTimestampFromIso(input.failedAt) || null,
  };
}

/* -----------------------------
   Firestore: USER PROFILE / META
------------------------------ */

// Ensure a minimal /users/{uid} document exists with meta + profile
export async function ensureUserDoc(uid, { email, displayName, photoURL } = {}) {
  if (!uid) return false;
  const refDoc = doc(db, 'users', uid);
  const snap = await getDoc(refDoc);

  const normalizedEmail =
    typeof email === 'string' ? email.trim().toLowerCase() : null;

  const base = {
    meta: { email: normalizedEmail },
    profile: {
      displayName: typeof displayName === 'string' ? displayName : null,
      photoURL: typeof photoURL === 'string' ? photoURL : null,
      updatedAt: serverTimestamp(),
    },
  };

  if (!snap.exists()) {
    await setDoc(
      refDoc,
      {
        ...base,
        meta: { ...base.meta, createdAt: serverTimestamp() },
      },
      { merge: true }
    );
  } else {
    await setDoc(refDoc, base, { merge: true });
  }
  return true;
}

// Merge-save profile at users/{uid}.profile
export async function setUserProfile(uid, data) {
  if (!uid) throw new Error('setUserProfile: missing uid');
  const refDoc = doc(db, 'users', uid);
  const safe = {
    profile: {
      displayName:
        typeof data?.displayName === 'string' ? data.displayName : null,
      photoURL: typeof data?.photoURL === 'string' ? data.photoURL : null,
      updatedAt: serverTimestamp(),
    },
  };
  await setDoc(refDoc, safe, { merge: true });
  return true;
}

/* -----------------------------
   Firestore: USER PREFS
------------------------------ */

export async function saveUserPrefs(uid, prefs) {
  if (!uid) throw new Error('saveUserPrefs: missing uid');
  const refDoc = doc(db, 'users', uid);
  const safe = {
    prefs: {
      isPublicProfile: !!prefs?.isPublicProfile,
      defaults: {
        isPublic: !!prefs?.defaults?.isPublic,
        isMediaPublic: !!prefs?.defaults?.isMediaPublic,
      },
      notifications: {
        emailSummaries:
          typeof prefs?.notifications?.emailSummaries === 'boolean'
            ? prefs.notifications.emailSummaries
            : true,
      },
      updatedAt: serverTimestamp(),
    },
  };
  await setDoc(refDoc, safe, { merge: true });
  return true;
}

export async function getUserPrefs(uid) {
  if (!uid) return null;
  const refDoc = doc(db, 'users', uid);
  const snap = await getDoc(refDoc);
  const data = snap.exists() ? snap.data() : null;
  return data?.prefs || null;
}

export function watchUserPrefs(uid, callback) {
  if (!uid) return () => {};
  const refDoc = doc(db, 'users', uid);
  const unsub = onSnapshot(refDoc, (snap) => {
    const data = snap.exists() ? snap.data() : null;
    callback?.(data?.prefs || null);
  });
  return unsub;
}

/* -----------------------------
   Firestore: CREATE / READ (memories)
// ----------------------------- */

export async function addMemoryDoc({
  userId,
  lat,
  lng,
  isPublic,
  isMediaPublic,
  city = null,
  region = null,
  locality = null,
  administrative_area = null,
  country = null,
  countryCode = null,
  placeName = null,
  formattedAddress = null,
  date = null,
  time = null,
  title = '',
  story = '',
  imageUrls = [],
}) {
  const cleanUrls = Array.isArray(imageUrls)
    ? imageUrls.filter((u) => typeof u === 'string' && u.trim().length > 0)
    : [];

  const docRef = await addDoc(collection(db, 'memories'), {
    userId,
    location: { lat, lng },
    isPublic: !!isPublic,
    isMediaPublic: !!isMediaPublic,
    city: city ?? null,
    region: region ?? null,
    locality: locality ?? null,
    administrative_area: administrative_area ?? null,
    country: country ?? null,
    countryCode: countryCode ?? null,
    placeName: placeName ?? null,
    formattedAddress: formattedAddress ?? null,
    date: date ?? null,
    time: time ?? null,
    title: title ?? '',
    story: story ?? '',
    imageUrls: cleanUrls,
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function fetchPublicMemories() {
  const qy = query(
    collection(db, 'memories'),
    where('isPublic', '==', true),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(qy);
  return snap.docs.map((d) => ({ id: d.id, firestoreId: d.id, ...d.data() }));
}

export async function fetchUserMemories(userId) {
  const qy = query(
    collection(db, 'memories'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(qy);
  return snap.docs.map((d) => ({ id: d.id, firestoreId: d.id, ...d.data() }));
}

export async function getMemoryById(id) {
  const refDoc = doc(db, 'memories', id);
  const pendingRef = doc(db, 'memories', id, 'runtime', 'nftPending');
  const snap = await getDoc(refDoc);
  if (!snap.exists()) return null;
  const pendingSnap = await getDoc(pendingRef).catch(() => null);
  return {
    id: snap.id,
    firestoreId: snap.id,
    ...snap.data(),
    nftPending: pendingSnap?.exists?.() ? pendingSnap.data() : null,
  };
}

/* -----------------------------
   Firestore: UPDATE / DELETE (memories)
// ----------------------------- */

export async function updateMemoryDoc(id, updates) {
  const refDoc = doc(db, 'memories', id);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) throw new Error('Memory not found.');

  const existing = snap.data() || {};
  if (isMemoryMarkedMintedInData(existing)) {
    throw new Error('Minted memories cannot be edited.');
  }
  const toSave = { ...updates };
  if (Array.isArray(toSave.imageUrls)) {
    toSave.imageUrls = normalizeImageUrls(toSave.imageUrls);
  }

  const shouldSyncMediaVisibility =
    Object.prototype.hasOwnProperty.call(toSave, 'isMediaPublic') ||
    Object.prototype.hasOwnProperty.call(toSave, 'imageUrls');

  if (shouldSyncMediaVisibility) {
    const nextImageUrls = Array.isArray(toSave.imageUrls)
      ? toSave.imageUrls
      : normalizeImageUrls(existing.imageUrls);
    const nextMediaPublic =
      typeof toSave.isMediaPublic === 'boolean'
        ? toSave.isMediaPublic
        : !!existing.isMediaPublic;

    await syncMemoryMediaVisibility(nextImageUrls, nextMediaPublic);
  }

  await setDoc(refDoc, toSave, { merge: true });
  return true;
}

export async function saveMemoryNftPending({
  memoryId,
  chainId = 80002,
  network = 'polygon-amoy',
  tokenUri = null,
  metadataCid = null,
  imageCid = null,
  provider = 'pinata',
  gatewayBase = null,
  gatewayUrl = null,
  ownerUid = null,
  mirror = null,
  visibilityPolicy = null,
  status = 'uploaded_unverified',
  verification = null,
} = {}) {
  if (!memoryId) throw new Error('saveMemoryNftPending: missing memoryId');

  const pendingRef = doc(db, 'memories', memoryId, 'runtime', 'nftPending');
  const now = safeNowTs();
  const pending = {
    status: status || 'uploaded_unverified',
    chainId: Number(chainId) || null,
    network: network || null,
    tokenUri: tokenUri ? String(tokenUri) : null,
    metadataCid: metadataCid ? String(metadataCid) : null,
    imageCid: imageCid ? String(imageCid) : null,
    provider: provider ? String(provider) : null,
    gatewayBase: gatewayBase ? String(gatewayBase) : null,
    gatewayUrl: gatewayUrl ? String(gatewayUrl) : null,
    ownerUid: ownerUid ? String(ownerUid) : null,
    mirror: normalizeNftMirror(mirror),
    visibilityPolicy: visibilityPolicy ? String(visibilityPolicy) : null,
    uploadedAt: now,
    updatedAt: now,
    verification: normalizeVerification(verification),
  };

  const payload = {
    ...pending,
    memoryId,
    updatedAt: serverTimestamp(),
  };
  await setDoc(pendingRef, payload, { merge: true });
  return true;
}

/**
 * Persist NFT mint results onto the memory doc.
 *
 * Writes to:
 *   memories/{memoryId}.nft.latest   (object)
 *   memories/{memoryId}.nft.mints[]  (history)
 *
 * RULES-SAFE NOTES:
 * - arrayUnion() payload MUST be plain data (no serverTimestamp()).
 * - mintedAt is stored as Firestore Timestamp (not ISO string) so rules can validate `timestamp`.
 * - Uses `contract` field name (common in rules), not `contractAddress`.
 * - Avoids extra keys (like ownerUid/savedAt string) that your rules may reject.
 */
export async function saveMemoryNftMint({
  memoryId,
  chainId = 80002,
  network = 'polygon-amoy',
  contract = null,
  contractAddress = null, // accepted input, normalized into `contract`
  tokenId = null,
  txHash = null,
  explorerUrl = null,
  tokenUri = null,
  metadataCid = null,
  imageCid = null,
  provider = null,
  gatewayUrl = null,
  toAddress = null,
  minterAddress = null,
  ownerUid = null,
  mintedAtIso = null,
  status = 'success',
  mirror = null,
} = {}) {
  if (!memoryId) throw new Error('saveMemoryNftMint: missing memoryId');

  const refDoc = doc(db, 'memories', memoryId);
  const mintedAtTs = toFsTimestampFromIso(mintedAtIso) || safeNowTs();

  const normalizedMirror = normalizeNftMirror(mirror);

  // Plain entry (safe for arrayUnion): NO serverTimestamp() here
  const historyEntry = {
    status: status || null,
    chainId: Number(chainId) || null,
    network: network || null,
    contract: contract ? String(contract) : (contractAddress ? String(contractAddress) : null),
    contractAddress: contractAddress ? String(contractAddress) : (contract ? String(contract) : null),
    tokenId: tokenId != null ? String(tokenId) : null,
    txHash: txHash ? String(txHash) : null,
    explorerUrl: explorerUrl ? String(explorerUrl) : null,
    tokenUri: tokenUri ? String(tokenUri) : null,
    metadataCid: metadataCid ? String(metadataCid) : null,
    imageCid: imageCid ? String(imageCid) : null,
    provider: provider ? String(provider) : null,
    gatewayUrl: gatewayUrl ? String(gatewayUrl) : null,
    toAddress: normAddr(toAddress),
    minterAddress: normAddr(minterAddress),
    ownerUid: ownerUid ? String(ownerUid) : null,
    mirror: normalizedMirror || null,
    mintedAt: mintedAtTs, // Timestamp is allowed as a value
    savedAt: mintedAtTs,
  };

  // Latest entry can include serverTimestamp in *sibling fields*, not inside arrayUnion
  const latestEntry = {
    ...historyEntry,
  };

  const minimalLatestEntry = {
    status: historyEntry.status,
    chainId: historyEntry.chainId,
    network: historyEntry.network,
    contract: historyEntry.contract,
    contractAddress: historyEntry.contractAddress,
    tokenId: historyEntry.tokenId,
    txHash: historyEntry.txHash,
    explorerUrl: historyEntry.explorerUrl,
    tokenUri: historyEntry.tokenUri,
    metadataCid: historyEntry.metadataCid,
    imageCid: historyEntry.imageCid,
    provider: historyEntry.provider,
    gatewayUrl: historyEntry.gatewayUrl,
    toAddress: historyEntry.toAddress,
    minterAddress: historyEntry.minterAddress,
    ownerUid: historyEntry.ownerUid,
    mirror: historyEntry.mirror,
    mintedAt: historyEntry.mintedAt,
    savedAt: historyEntry.savedAt,
  };

  const latestMinimalWrite = async () =>
    setDoc(
      refDoc,
      {
        nft: {
          latest: minimalLatestEntry,
          updatedAt: serverTimestamp(),
        },
        nftPending: null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

  const fullWrite = async () =>
    setDoc(
      refDoc,
      {
        nft: {
          latest: latestEntry,
          updatedAt: serverTimestamp(),
        },
        nftPending: null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

  try {
    await setDoc(
      refDoc,
      {
        nft: {
          latest: latestEntry,
          mints: arrayUnion(historyEntry),
          updatedAt: serverTimestamp(),
        },
        nftPending: null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    await deleteDoc(doc(db, 'memories', memoryId, 'runtime', 'nftPending')).catch(() => {});
    return true;
  } catch (err) {
    if (err?.code !== 'permission-denied') {
      throw err;
    }
  }

  try {
    await fullWrite();
    await deleteDoc(doc(db, 'memories', memoryId, 'runtime', 'nftPending')).catch(() => {});
    return true;
  } catch (err) {
    if (err?.code !== 'permission-denied') {
      throw err;
    }
  }

  await latestMinimalWrite();
  await deleteDoc(doc(db, 'memories', memoryId, 'runtime', 'nftPending')).catch(() => {});
  return true;
}

export async function deleteMemoryAndFiles(id) {
  const refDoc = doc(db, 'memories', id);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) {
    try {
      await deleteDoc(refDoc);
    } catch (_) {}
    return true;
  }
  const data = snap.data() || {};
  if (isMemoryMarkedMintedInData(data)) {
    throw new Error('Minted memories cannot be deleted.');
  }
  const raw = Array.isArray(data.imageUrls) ? data.imageUrls : [];
  const urls = raw.filter(
    (u) => typeof u === 'string' && u.trim().length > 0 && isFirebaseUrl(u)
  );

  await Promise.all(
    urls.map(async (url) => {
      try {
        const fileRef = ref(storage, url);
        await deleteObject(fileRef);
      } catch (e) {
        console.warn('Skip deleting file:', url, e?.message || e);
      }
    })
  );
  await deleteDoc(refDoc);
  return true;
}

/* -----------------------------
   Storage helpers (media & avatar)
// ----------------------------- */

export function uploadFile(file, uid, onProgress) {
  const opts =
    typeof onProgress === 'function'
      ? { onProgress }
      : (onProgress && typeof onProgress === 'object' ? onProgress : {});
  const progressHandler = typeof opts.onProgress === 'function' ? opts.onProgress : null;
  const mediaPublic = opts.mediaPublic === true;

  return new Promise((resolve, reject) => {
    try {
      const now = new Date();
      const folder = `${now.getFullYear()}${String(
        now.getMonth() + 1
      ).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      const cleanName = `${Date.now()}-${file.name.replace(/\s+/g, '_')}`;
      const path = `user_uploads/${uid}/${folder}/${cleanName}`;

      const fileRef = ref(storage, path);
      const task = uploadBytesResumable(fileRef, file, {
        contentType: file.type || 'application/octet-stream',
        cacheControl: 'public,max-age=31536000',
        customMetadata: {
          mediaPublic: mediaPublic ? 'true' : 'false',
        },
      });

      task.on(
        'state_changed',
        (snap) => {
          if (progressHandler) {
            const pct = Math.round(
              (snap.bytesTransferred / snap.totalBytes) * 100
            );
            progressHandler(pct);
          }
        },
        (err) => reject(err),
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            resolve(url);
          } catch (e) {
            reject(e);
          }
        }
      );
    } catch (e) {
      reject(e);
    }
  });
}

// Upload avatar to a separate path (avatar_uploads/..)
export function uploadUserAvatar(uid, file, onProgress) {
  return new Promise((resolve, reject) => {
    try {
      const cleanName = `${Date.now()}-${file.name.replace(/\s+/g, '_')}`;
      const path = `avatar_uploads/${uid}/${cleanName}`;
      const fileRef = ref(storage, path);
      const task = uploadBytesResumable(fileRef, file, {
        contentType: file.type || 'application/octet-stream',
        cacheControl: 'public,max-age=31536000',
      });

      task.on(
        'state_changed',
        (snap) => {
          if (onProgress) {
            const pct = Math.round(
              (snap.bytesTransferred / snap.totalBytes) * 100
            );
            onProgress(pct);
          }
        },
        (err) => reject(err),
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            resolve(url);
          } catch (e) {
            reject(e);
          }
        }
      );
    } catch (e) {
      reject(e);
    }
  });
}

export async function uploadFiles(uid, files, onProgress) {
  const opts =
    typeof onProgress === 'function'
      ? { onProgress }
      : (onProgress && typeof onProgress === 'object' ? onProgress : {});
  const progressHandler = typeof opts.onProgress === 'function' ? opts.onProgress : null;
  const mediaPublic = opts.mediaPublic === true;

  if (!uid) throw new Error('Missing uid for uploadFiles');
  if (!files || files.length === 0) return [];
  const uploads = [...files].map((file) => {
    const safeName = `${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
    const path = `user_uploads/${uid}/${safeName}`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type || 'application/octet-stream',
      customMetadata: {
        mediaPublic: mediaPublic ? 'true' : 'false',
      },
    });
    return new Promise((resolve, reject) => {
      task.on(
        'state_changed',
        (snap) => {
          if (progressHandler) {
            const pct = Math.round(
              (snap.bytesTransferred / snap.totalBytes) * 100
            );
            progressHandler({ file, percent: pct });
          }
        },
        (err) =>
          reject(new Error(`Upload failed for ${file.name}: ${err.message}`)),
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            resolve(url);
          } catch (e) {
            reject(
              new Error(
                `Could not get download URL for ${file.name}: ${e.message}`
              )
            );
          }
        }
      );
    });
  });
  return Promise.all(uploads);
}

/* -----------------------------
   Admin helpers (list users + memories)
// ----------------------------- */

// Users – ordered by meta.email for search/pagination
export async function adminListUsers({
  pageSize = 25,
  afterEmail = null,
  search = '',
} = {}) {
  let qBase = collection(db, 'users');
  let qy;

  const normalized = (search || '').trim().toLowerCase();
  if (normalized) {
    const end = normalized.replace(/.$/, (c) =>
      String.fromCharCode(c.charCodeAt(0) + 1)
    );
    qy = query(
      qBase,
      orderBy('meta.email'),
      where('meta.email', '>=', normalized),
      where('meta.email', '<', end),
      limit(pageSize)
    );
  } else {
    qy = afterEmail
      ? query(qBase, orderBy('meta.email'), startAfter(afterEmail), limit(pageSize))
      : query(qBase, orderBy('meta.email'), limit(pageSize));
  }

  const snap = await getDocs(qy);
  const rows = snap.docs.map((d) => ({ id: d.id, uid: d.id, ...d.data() }));
  const next = rows.length === pageSize ? rows[rows.length - 1]?.meta?.email || null : null;
  return { rows, next };
}

// Memories – global listing with optional public filter
export async function adminListMemories({
  pageSize = 50,
  afterCreatedAt = null,
  publicOnly = null,
} = {}) {
  let qBase = collection(db, 'memories');
  const parts = [orderBy('createdAt', 'desc')];
  if (publicOnly === true) parts.unshift(where('isPublic', '==', true));
  if (publicOnly === false) parts.unshift(where('isPublic', '==', false));
  if (afterCreatedAt) parts.push(startAfter(afterCreatedAt));
  parts.push(limit(pageSize));
  const snap = await getDocs(query(qBase, ...parts));
  const rows = snap.docs.map((d) => ({ id: d.id, firestoreId: d.id, ...d.data() }));
  const next = rows.length === pageSize ? rows[rows.length - 1]?.createdAt || null : null;
  return { rows, next };
}

/* -----------------------------
   Moderation flags
// ----------------------------- */

export async function createFlag({
  memoryId,
  reporterUid,
  ownerUid,
  reason,
  note = null,
  memoryTitle = null,
}) {
  if (!memoryId || !reporterUid || !ownerUid || !reason)
    throw new Error('Missing fields');
  const qy = query(
    collection(db, 'flags'),
    where('memoryId', '==', memoryId),
    where('reporterUid', '==', reporterUid),
    where('status', '==', 'open'),
    limit(1)
  );
  const snap = await getDocs(qy);
  if (!snap.empty) {
    return { dedupe: true, id: snap.docs[0].id };
  }
  const ref = await addDoc(collection(db, 'flags'), {
    memoryId,
    reporterUid,
    ownerUid,
    reason,
    note: note || null,
    memoryTitle: memoryTitle || null,
    status: 'open',
    createdAt: serverTimestamp(),
  });
  return { id: ref.id };
}

export async function queryFlags({
  status = 'open',
  limitN = 50,
  afterCreatedAt = null,
} = {}) {
  const col = collection(db, 'flags');
  const parts = [];
  if (status !== 'all') parts.push(where('status', '==', status));
  parts.push(orderBy('createdAt', 'desc'));
  if (afterCreatedAt) parts.push(startAfter(afterCreatedAt));
  parts.push(limit(limitN));

  const snap = await getDocs(query(col, ...parts));
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const nextCursor = rows.length ? rows[rows.length - 1]?.createdAt || null : null;
  return { rows, nextCursor };
}

/* -----------------------------
   Admin Activity
// ----------------------------- */

export async function logAdminActivity({
  action,
  actorUid = null,
  targetUid = null,
  details = null,
}) {
  await addDoc(collection(db, 'admin_activity'), {
    action,
    actorUid: actorUid || null,
    targetUid: targetUid || null,
    details: details || null,
    createdAt: serverTimestamp(),
  });
}

export async function resolveFlag(
  flagId,
  { newStatus = 'resolved', resolutionNote = null, resolvedByUid = null } = {}
) {
  if (!flagId) throw new Error('Missing flagId');
  const allowed = new Set(['open', 'resolved']);
  const next = allowed.has(newStatus) ? newStatus : 'resolved';

  const refD = doc(db, 'flags', flagId);
  await setDoc(
    refD,
    {
      status: next,
      resolutionNote: resolutionNote || null,
      resolvedByUid: resolvedByUid || null,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  try {
    await logAdminActivity({
      action: next === 'resolved' ? 'resolve_flag' : 'reopen_flag',
      actorUid: resolvedByUid || null,
      targetUid: flagId,
      details: { newStatus: next, resolutionNote },
    });
  } catch (_) {}
  return true;
}

/* -----------------------------
   Admin moderation helpers
// ----------------------------- */

export async function adminHideMemory({ memoryId, actorUid, ownerUid }) {
  if (!memoryId) throw new Error('Missing memoryId');
  const snap = await getDoc(doc(db, 'memories', memoryId));
  const existing = snap.exists() ? snap.data() : null;
  await syncMemoryMediaVisibility(existing?.imageUrls || [], false);
  await setDoc(
    doc(db, 'memories', memoryId),
    {
      isPublic: false,
      isMediaPublic: false,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  try {
    await logAdminActivity({
      action: 'hide_memory',
      actorUid: actorUid || null,
      targetUid: ownerUid || null,
      details: { memoryId, isPublic: false, isMediaPublic: false },
    });
  } catch (_) {}
  return true;
}

export async function adminUnhideMemory({ memoryId, actorUid, ownerUid }) {
  if (!memoryId) throw new Error('Missing memoryId');
  const snap = await getDoc(doc(db, 'memories', memoryId));
  const existing = snap.exists() ? snap.data() : null;
  await syncMemoryMediaVisibility(existing?.imageUrls || [], true);
  await setDoc(
    doc(db, 'memories', memoryId),
    {
      isPublic: true,
      isMediaPublic: true,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  try {
    await logAdminActivity({
      action: 'unhide_memory',
      actorUid: actorUid || null,
      targetUid: ownerUid || null,
      details: { memoryId, isPublic: true, isMediaPublic: true },
    });
  } catch (_) {}
  return true;
}

export async function adminDeleteMemory({ memoryId, actorUid, ownerUid }) {
  if (!memoryId) throw new Error('Missing memoryId');

  await (async () => {
    const refDoc = doc(db, 'memories', memoryId);
    const snap = await getDoc(refDoc);
    if (snap.exists()) {
      const data = snap.data() || {};
      const raw = Array.isArray(data.imageUrls) ? data.imageUrls : [];
      const urls = raw.filter(
        (u) => typeof u === 'string' && u.trim().length > 0 && isFirebaseUrl(u)
      );
      await Promise.all(
        urls.map(async (url) => {
          try {
            await deleteObject(ref(storage, url));
          } catch (_) {}
        })
      );
    }
    await deleteDoc(refDoc);
  })();

  try {
    await logAdminActivity({
      action: 'delete_memory',
      actorUid: actorUid || null,
      targetUid: ownerUid || null,
      details: { memoryId },
    });
  } catch (_) {}
  return true;
}
// ==========================
// End Version 15 — db.js
// ==========================
